import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dumbbell, Activity, Target, ChevronRight, Sparkles, Clock,
  BookmarkPlus, Bookmark, Trash2, Flame, Zap, Wind, ArrowLeft,
  Loader2, ChevronDown, ChevronUp, TrendingUp, Calendar,
  CheckCircle2, BarChart3, Timer, Trophy, Heart, Plus, Minus,
  MoveUp, MoveDown, X, Replace, Share2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import Navigation from '../components/Navigation';
import TooltipGuide from '../components/TooltipGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Illustration map for known exercises
const EXERCISE_ILLUSTRATIONS = {
  'dead bug': 'https://static.prod-images.emergentagent.com/jobs/abaef1a9-248c-4568-ae89-2388580d687e/images/d65819dbcde500219137e3b9d31bfa55a06071f3d84040b1b720e68059df5a45.png',
  "world's greatest stretch": 'https://static.prod-images.emergentagent.com/jobs/abaef1a9-248c-4568-ae89-2388580d687e/images/565f67acea589fd6b0756fb728638d349fbfe68f8c2c78e030c5dfd23a6be29e.png',
  'single-leg romanian deadlift': 'https://static.prod-images.emergentagent.com/jobs/abaef1a9-248c-4568-ae89-2388580d687e/images/82b9de67e7ead5fbde863a9060f5475000539f36cea91eba802865a42b395791.png',
  'single leg romanian deadlift': 'https://static.prod-images.emergentagent.com/jobs/abaef1a9-248c-4568-ae89-2388580d687e/images/82b9de67e7ead5fbde863a9060f5475000539f36cea91eba802865a42b395791.png',
  'cat-cow': 'https://static.prod-images.emergentagent.com/jobs/abaef1a9-248c-4568-ae89-2388580d687e/images/2b5531426f785a4d5df49dc1ae91db2f2d8c0e3bf2d04f740b5b1da3cf61e9c3.png',
  'cat cow': 'https://static.prod-images.emergentagent.com/jobs/abaef1a9-248c-4568-ae89-2388580d687e/images/2b5531426f785a4d5df49dc1ae91db2f2d8c0e3bf2d04f740b5b1da3cf61e9c3.png',
};

const getIllustration = (exerciseName) => {
  const nameLower = (exerciseName || '').toLowerCase();
  for (const [key, url] of Object.entries(EXERCISE_ILLUSTRATIONS)) {
    if (nameLower.includes(key)) return url;
  }
  return null;
};

// YouTube video map for select exercises (embed IDs)
const EXERCISE_VIDEOS = {
  'band pull-apart': { type: 'youtube', id: 'FH41yS4MHQ8' },
  'band pull apart': { type: 'youtube', id: 'FH41yS4MHQ8' },
  'lateral band walk': { type: 'youtube', id: '6h3TVFPTSQo' },
  '90/90 hip switch': { type: 'youtube', id: 'BzFbec-I8qU' },
  '90/90 hip': { type: 'youtube', id: 'BzFbec-I8qU' },
  'open book': { type: 'youtube', id: 'PkybXG0TNJ8' },
  'open book stretch': { type: 'youtube', id: 'PkybXG0TNJ8' },
  'anti-rotation press': { type: 'sora', src: `${process.env.REACT_APP_BACKEND_URL}/api/photos/sora_anti_rotation_press.mp4` },
  'anti rotation press': { type: 'sora', src: `${process.env.REACT_APP_BACKEND_URL}/api/photos/sora_anti_rotation_press.mp4` },
};

const getVideo = (exerciseName) => {
  const nameLower = (exerciseName || '').toLowerCase();
  for (const [key, val] of Object.entries(EXERCISE_VIDEOS)) {
    if (nameLower.includes(key)) return val;
  }
  return null;
};

const FOCUS_AREAS = [
  { id: 'full_body', label: 'Full Body' },
  { id: 'hips', label: 'Hips' },
  { id: 'shoulders', label: 'Shoulders' },
  { id: 'core', label: 'Core' },
  { id: 'back', label: 'Back' },
  { id: 'wrists', label: 'Wrists' },
  { id: 'legs', label: 'Legs' },
  { id: 'chest', label: 'Chest' },
  { id: 'glutes', label: 'Glutes' },
  { id: 'forearms', label: 'Forearms' },
  { id: 'neck', label: 'Neck/Traps' },
];

const FITNESS_LEVELS = [
  { id: 'beginner', label: 'Beginner', desc: 'New to fitness' },
  { id: 'intermediate', label: 'Intermediate', desc: 'Regular exerciser' },
  { id: 'advanced', label: 'Advanced', desc: 'Experienced athlete' },
];

const STRENGTH_DURATIONS = [10, 15, 20, 30, 45, 60];
const MOBILITY_DURATIONS = { pre_round: [5, 10, 15, 20, 30, 60], post_round: [5, 10, 15, 20, 30, 60], flexibility: [10, 15, 20, 30, 45, 60] };

const SUBCATEGORY_INFO = {
  strength: {
    home: { label: 'Home Workout', desc: 'Minimal or no equipment needed', icon: Flame },
    gym: { label: 'Gym Workout', desc: 'Full gym equipment', icon: Dumbbell },
  },
  mobility: {
    pre_round: { label: 'Pre-Round Warmup', desc: 'Dynamic stretches before you play', icon: Zap },
    post_round: { label: 'Post-Round Recovery', desc: 'Cool down after your round', icon: Wind },
    flexibility: { label: 'Overall Flexibility', desc: 'Improve your range of motion', icon: Activity },
  },
};

const RECOVERY_COLORS = { sore: 'text-red-500 bg-red-500/10 border-red-500/30', recovering: 'text-yellow-500 bg-yellow-500/10 border-yellow-500/30', recovered: 'text-green-500 bg-green-500/10 border-green-500/30' };
const RECOVERY_LABELS = { sore: 'Sore (<24h)', recovering: 'Recovering', recovered: 'Ready' };

// ===== ExerciseCard =====
const ExerciseCard = ({ exercise, index, isLogging = false, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const illustration = getIllustration(exercise.name);
  const video = getVideo(exercise.name);

  // Initialize logged_sets from exercise data when logging starts
  const getDefaultSets = () => {
    if (exercise.logged_sets) return exercise.logged_sets;
    const numSets = exercise.sets || 3;
    // Parse reps to a number — take first number from string like "10-12" or "8 each side"
    const rawReps = String(exercise.reps || '');
    const repsMatch = rawReps.match(/\d+/);
    const defaultReps = repsMatch ? repsMatch[0] : '';
    // Parse suggested_weight to a number; 0 or null means bodyweight
    let defaultWeight = '';
    const sw = exercise.suggested_weight;
    if (sw === 0 || sw === '0' || sw === 'bodyweight' || sw === 'BW' || sw == null) {
      defaultWeight = '0';
    } else if (typeof sw === 'number') {
      defaultWeight = String(sw);
    } else if (typeof sw === 'string') {
      const num = sw.match(/\d+/);
      defaultWeight = num ? num[0] : '0';
    }
    return Array.from({ length: numSets }, () => ({ reps: String(defaultReps), weight: defaultWeight }));
  };

  const loggedSets = exercise.logged_sets || getDefaultSets();

  const updateSet = (setIdx, field, value) => {
    const updated = loggedSets.map((s, i) => i === setIdx ? { ...s, [field]: value } : s);
    onUpdate(index, 'logged_sets', updated);
  };

  const addSet = () => {
    const lastSet = loggedSets[loggedSets.length - 1] || { reps: '', weight: '' };
    onUpdate(index, 'logged_sets', [...loggedSets, { reps: lastSet.reps, weight: lastSet.weight }]);
  };

  const removeSet = (setIdx) => {
    if (loggedSets.length <= 1) return;
    onUpdate(index, 'logged_sets', loggedSets.filter((_, i) => i !== setIdx));
  };

  const hasAiVideo = !!exercise.video_path;
  const aiVideoUrl = hasAiVideo ? `${API}/fitness/exercise-video/${exercise.id}` : null;
  const hasAiImage = !!exercise.illustration_url;
  const aiImageUrl = hasAiImage ? `${API}/fitness/exercise-image/${exercise.id}` : null;

  return (
    <Card className="border-border/50 overflow-hidden transition-all duration-200 hover:border-primary/30" data-testid={`exercise-card-${index}`}>
      <CardContent className="p-0">
        <button className="w-full text-left p-4 flex items-start gap-3" onClick={() => setExpanded(!expanded)} data-testid={`exercise-toggle-${index}`}>
          {hasAiVideo ? (
            <div className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden bg-black relative">
              <video src={aiVideoUrl} muted loop autoPlay playsInline className="w-full h-full object-cover" />
            </div>
          ) : hasAiImage ? (
            <div className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden bg-muted relative">
              <img src={aiImageUrl} alt={exercise.name} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ) : illustration || video ? (
            <div className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden bg-muted relative">
              {illustration && !video ? (
                <img src={illustration} alt={exercise.name} className="w-full h-full object-cover" />
              ) : video?.type === 'youtube' ? (
                <img src={`https://img.youtube.com/vi/${video.id}/default.jpg`} alt={exercise.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-purple-500/20 to-primary/20 flex items-center justify-center"><span className="text-[8px] font-bold text-primary">AI</span></div>
              )}
              {video && <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-xl"><div className="w-5 h-5 bg-red-600 rounded-sm flex items-center justify-center"><span className="text-white text-[8px] ml-0.5">&#9654;</span></div></div>}
            </div>
          ) : (
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">{index + 1}</div>
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-foreground text-sm">{exercise.name}</h4>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{exercise.description}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {exercise.sets && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{exercise.sets} sets</Badge>}
              {exercise.reps && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{exercise.reps}</Badge>}
              {exercise.suggested_weight != null && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {exercise.suggested_weight === 0 || exercise.suggested_weight === '0' || exercise.suggested_weight === 'bodyweight' ? 'BW' : `${exercise.suggested_weight} lbs`}
                </Badge>
              )}
              {exercise.duration_seconds && !exercise.sets && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{exercise.duration_seconds}s</Badge>}
            </div>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />}
        </button>

        {expanded && (
          <div className="px-4 pb-4 border-t border-border/30 pt-3 space-y-3 animate-in fade-in duration-200">
            {/* AI-generated exercise video (FitBod style) */}
            {hasAiVideo && (
              <div className="flex justify-center" data-testid={`exercise-ai-video-${index}`}>
                <div className="w-full rounded-xl overflow-hidden bg-black" style={{ maxWidth: '360px', aspectRatio: '16/9' }}>
                  <video src={aiVideoUrl} autoPlay loop muted playsInline className="w-full h-full object-cover" />
                </div>
              </div>
            )}
            {/* AI-generated illustration */}
            {!hasAiVideo && hasAiImage && (
              <div className="flex justify-center" data-testid={`exercise-ai-image-${index}`}>
                <div className="w-full rounded-xl overflow-hidden bg-muted" style={{ maxWidth: '360px' }}>
                  <img src={aiImageUrl} alt={exercise.name} className="w-full h-auto object-cover rounded-xl" />
                </div>
              </div>
            )}
            {/* YouTube or other video */}
            {!hasAiVideo && !hasAiImage && video && (
              <div className="flex justify-center" data-testid={`exercise-video-${index}`}>
                {video.type === 'youtube' ? (
                  <div className="w-full rounded-xl overflow-hidden bg-black" style={{ aspectRatio: '16/9', maxWidth: '360px' }}>
                    <iframe
                      src={`https://www.youtube.com/embed/${video.id}?rel=0&modestbranding=1`}
                      title={exercise.name}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full border-0"
                    />
                  </div>
                ) : (
                  <div className="w-full rounded-xl overflow-hidden bg-black" style={{ maxWidth: '360px' }}>
                    <video
                      src={video.src}
                      controls
                      loop
                      playsInline
                      className="w-full rounded-xl"
                      style={{ maxHeight: '280px' }}
                    >
                      Your browser does not support video playback.
                    </video>
                    <div className="text-center py-1">
                      <Badge className="bg-purple-500/10 text-purple-500 border-purple-500/30 text-[10px]">AI Generated (Sora 2)</Badge>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!video && illustration && (
              <div className="flex justify-center">
                <img src={illustration} alt={exercise.name} className="w-40 h-40 object-contain rounded-xl bg-muted/50" />
              </div>
            )}
            <div>
              <h5 className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Instructions</h5>
              <ol className="space-y-1.5">
                {(exercise.instructions || []).map((step, i) => (
                  <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                    <span className="text-primary font-medium flex-shrink-0">{i + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
            {exercise.muscle_groups?.length > 0 && (
              <div>
                <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Muscles</h5>
                <div className="flex flex-wrap gap-1">
                  {exercise.muscle_groups.map((m, i) => <Badge key={i} className="text-[10px] capitalize bg-primary/10 text-primary border-0">{m}</Badge>)}
                </div>
              </div>
            )}
            {isLogging && onUpdate && (
              <div className="space-y-2 pt-2 border-t border-border/30">
                <h5 className="text-xs font-semibold text-primary uppercase tracking-wider">Log Your Sets</h5>
                {/* Header row */}
                <div className="grid grid-cols-[2rem_1fr_1fr_2rem] gap-2 items-center">
                  <span className="text-[10px] text-muted-foreground text-center">Set</span>
                  <span className="text-[10px] text-muted-foreground">Reps</span>
                  <span className="text-[10px] text-muted-foreground">Weight (lbs)</span>
                  <span></span>
                </div>
                {/* Set rows */}
                {loggedSets.map((set, si) => (
                  <div key={si} className="grid grid-cols-[2rem_1fr_1fr_2rem] gap-2 items-center" data-testid={`set-row-${index}-${si}`}>
                    <span className="text-xs font-semibold text-primary text-center">{si + 1}</span>
                    <Input
                      type="number"
                      min="0"
                      className="h-8 text-sm"
                      value={set.reps}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d+$/.test(val)) updateSet(si, 'reps', val);
                      }}
                      placeholder={String(exercise.reps || '0').replace(/\D.*/, '') || '0'}
                      data-testid={`log-reps-${index}-${si}`}
                    />
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        className="h-8 text-sm pr-8"
                        value={set.weight}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '' || /^\d+$/.test(val)) updateSet(si, 'weight', val);
                        }}
                        placeholder="0"
                        data-testid={`log-weight-${index}-${si}`}
                      />
                      {(set.weight === '0' || set.weight === '') && (
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">BW</span>
                      )}
                    </div>
                    <button
                      onClick={() => removeSet(si)}
                      className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-30"
                      disabled={loggedSets.length <= 1}
                      data-testid={`remove-set-${index}-${si}`}
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {/* Add set button */}
                <button
                  onClick={addSet}
                  className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-dashed border-primary/30 text-primary text-xs font-medium hover:bg-primary/5 transition-colors"
                  data-testid={`add-set-${index}`}
                >
                  <Plus className="w-3.5 h-3.5" /> Add Set
                </button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===== RoutineView =====
const RoutineView = ({ routine, onSave, onBack, isSaved, onDelete, saving, onComplete, exerciseLibrary, onFetchLibrary }) => {
  const [logging, setLogging] = useState(false);
  const [exercises, setExercises] = useState(routine.exercises || []);
  const [completing, setCompleting] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [replaceIdx, setReplaceIdx] = useState(null); // index of exercise being replaced, null = add new

  useEffect(() => { setExercises(routine.exercises || []); setLogging(false); }, [routine]);

  const toggleLogging = () => {
    if (!logging) {
      setExercises(prev => prev.map(ex => {
        if (ex.logged_sets) return ex;
        const numSets = ex.sets || 3;
        const rawReps = String(ex.reps || '');
        const repsMatch = rawReps.match(/\d+/);
        const defaultReps = repsMatch ? repsMatch[0] : '';
        const sw = ex.suggested_weight;
        let defaultWeight = '0';
        if (typeof sw === 'number') defaultWeight = String(sw);
        else if (typeof sw === 'string') { const n = sw.match(/\d+/); defaultWeight = n ? n[0] : '0'; }
        return { ...ex, logged_sets: Array.from({ length: numSets }, () => ({ reps: defaultReps, weight: defaultWeight })) };
      }));
    }
    setLogging(!logging);
  };

  const updateExercise = (idx, field, value) => {
    setExercises(prev => prev.map((ex, i) => i === idx ? { ...ex, [field]: value } : ex));
  };

  const moveExercise = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= exercises.length) return;
    setExercises(prev => {
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  };

  const removeExercise = (idx) => {
    if (exercises.length <= 1) { toast.error('Need at least one exercise'); return; }
    setExercises(prev => prev.filter((_, i) => i !== idx));
  };

  const openLibraryToReplace = (idx) => {
    setReplaceIdx(idx);
    if (onFetchLibrary) onFetchLibrary();
    setShowLibrary(true);
  };

  const openLibraryToAdd = () => {
    setReplaceIdx(null);
    if (onFetchLibrary) onFetchLibrary();
    setShowLibrary(true);
  };

  const selectFromLibrary = (libExercise) => {
    if (replaceIdx !== null) {
      setExercises(prev => prev.map((ex, i) => i === replaceIdx ? { ...libExercise } : ex));
    } else {
      setExercises(prev => [...prev, { ...libExercise }]);
    }
    setShowLibrary(false);
    setReplaceIdx(null);
  };

  const handleComplete = async () => {
    setCompleting(true);
    const enriched = exercises.map(ex => ({
      ...ex,
      actual_sets: ex.logged_sets?.length || ex.sets || 0,
      actual_reps: ex.logged_sets?.[0]?.reps || ex.reps || '',
      weight: ex.logged_sets?.[0]?.weight || '',
    }));
    await onComplete(enriched);
    setCompleting(false);
    setLogging(false);
  };

  // Library picker overlay
  if (showLibrary) {
    const grouped = {};
    (exerciseLibrary || []).forEach(ex => { const a = ex.focus_area || 'other'; if (!grouped[a]) grouped[a] = []; grouped[a].push(ex); });

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setShowLibrary(false)} data-testid="library-picker-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-lg font-bold text-foreground">{replaceIdx !== null ? 'Replace Exercise' : 'Add Exercise'}</h2>
            <p className="text-sm text-muted-foreground">Tap an exercise to select it</p>
          </div>
        </div>
        {Object.entries(grouped).map(([area, exs]) => (
          <div key={area}>
            <h3 className="text-sm font-semibold text-primary uppercase tracking-wider mb-2 capitalize">{area}</h3>
            <div className="space-y-1.5">
              {exs.map((ex, i) => (
                <button key={ex.id || i} onClick={() => selectFromLibrary(ex)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-card border border-border/50 hover:border-primary/40 hover:bg-primary/5 transition-all text-left" data-testid={`lib-pick-${area}-${i}`}>
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground">{ex.name}</h4>
                    <p className="text-xs text-muted-foreground line-clamp-1">{ex.description}</p>
                  </div>
                  <Plus className="w-4 h-4 text-primary flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ))}
        {(exerciseLibrary || []).length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">No exercises in library yet. Generate a routine first to populate it.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="routine-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-foreground">{routine.title}</h2>
          <div className="flex gap-2 mt-1">
            <Badge variant="outline" className="capitalize text-xs">{routine.category}</Badge>
            <Badge className="bg-primary/10 text-primary border-0 capitalize text-xs">{routine.subcategory?.replace('_', '-')}</Badge>
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {!isSaved && <Button size="sm" onClick={onSave} disabled={saving} data-testid="save-routine-btn" className="bg-primary hover:bg-primary/90">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4 mr-1" />}Save</Button>}
        {isSaved && <Button size="sm" variant="destructive" onClick={onDelete} data-testid="delete-routine-btn"><Trash2 className="w-4 h-4 mr-1" />Remove</Button>}
        <Button size="sm" variant={logging ? "default" : "outline"} onClick={toggleLogging} data-testid="toggle-logging-btn" className={logging ? "bg-primary" : ""}>
          {logging ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <BarChart3 className="w-4 h-4 mr-1" />}
          {logging ? 'Logging' : 'Log Workout'}
        </Button>
      </div>

      {routine.warmup_notes && (
        <Card className="border-yellow-500/30 bg-yellow-500/5">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1"><Zap className="w-4 h-4 text-yellow-500" /><span className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">Warm Up</span></div>
            <p className="text-sm text-muted-foreground">{routine.warmup_notes}</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {exercises.map((ex, i) => (
          <div key={i}>
            <ExerciseCard exercise={ex} index={i} isLogging={logging} onUpdate={updateExercise} />
            {/* Exercise controls: move, replace, remove */}
            <div className="flex items-center justify-center gap-1 mt-1 mb-1">
              <button onClick={() => moveExercise(i, -1)} disabled={i === 0} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors" data-testid={`move-up-${i}`} title="Move up">
                <MoveUp className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => moveExercise(i, 1)} disabled={i === exercises.length - 1} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors" data-testid={`move-down-${i}`} title="Move down">
                <MoveDown className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => openLibraryToReplace(i)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-primary transition-colors" data-testid={`replace-exercise-${i}`} title="Replace exercise">
                <Replace className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => removeExercise(i)} disabled={exercises.length <= 1} className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-20 transition-colors" data-testid={`remove-exercise-${i}`} title="Remove exercise">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add exercise from library */}
      <button onClick={openLibraryToAdd} className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-primary/30 text-primary text-sm font-medium hover:bg-primary/5 transition-colors" data-testid="add-exercise-btn">
        <Plus className="w-4 h-4" /> Add Exercise from Library
      </button>

      {routine.cooldown_notes && (
        <Card className="border-blue-500/30 bg-blue-500/5">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1"><Wind className="w-4 h-4 text-blue-500" /><span className="text-sm font-semibold text-blue-600 dark:text-blue-400">Cool Down</span></div>
            <p className="text-sm text-muted-foreground">{routine.cooldown_notes}</p>
          </CardContent>
        </Card>
      )}

      {logging && (
        <Button className="w-full h-12 text-base font-semibold bg-green-600 hover:bg-green-700 text-white" onClick={handleComplete} disabled={completing} data-testid="complete-workout-btn">
          {completing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
          Complete Workout
        </Button>
      )}
    </div>
  );
};

// ===== ProgressView =====
const ProgressView = ({ onBack, stats, history }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-3">
      <Button variant="ghost" size="icon" onClick={onBack} data-testid="progress-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
      <h2 className="text-lg font-bold text-foreground">Progress</h2>
    </div>

    {/* Stats Cards */}
    <div className="grid grid-cols-2 gap-3">
      <Card className="border-orange-500/30 bg-orange-500/5">
        <CardContent className="p-4 text-center">
          <Flame className="w-6 h-6 text-orange-500 mx-auto mb-1" />
          <div className="text-2xl font-bold text-foreground" data-testid="streak-count">{stats.streak}</div>
          <div className="text-xs text-muted-foreground">Day Streak</div>
        </CardContent>
      </Card>
      <Card className="border-blue-500/30 bg-blue-500/5">
        <CardContent className="p-4 text-center">
          <Calendar className="w-6 h-6 text-blue-500 mx-auto mb-1" />
          <div className="text-2xl font-bold text-foreground" data-testid="weekly-workouts">{stats.weekly_workouts}</div>
          <div className="text-xs text-muted-foreground">This Week</div>
        </CardContent>
      </Card>
      <Card className="border-green-500/30 bg-green-500/5">
        <CardContent className="p-4 text-center">
          <Timer className="w-6 h-6 text-green-500 mx-auto mb-1" />
          <div className="text-2xl font-bold text-foreground" data-testid="weekly-duration">{stats.weekly_duration}m</div>
          <div className="text-xs text-muted-foreground">Weekly Duration</div>
        </CardContent>
      </Card>
      <Card className="border-purple-500/30 bg-purple-500/5">
        <CardContent className="p-4 text-center">
          <TrendingUp className="w-6 h-6 text-purple-500 mx-auto mb-1" />
          <div className="text-2xl font-bold text-foreground" data-testid="weekly-volume">{stats.weekly_volume?.toLocaleString() || 0}</div>
          <div className="text-xs text-muted-foreground">Weekly Volume (lbs)</div>
        </CardContent>
      </Card>
    </div>

    <Card>
      <CardContent className="p-4 text-center">
        <Trophy className="w-6 h-6 text-primary mx-auto mb-1" />
        <div className="text-xl font-bold text-foreground">{stats.total_workouts}</div>
        <div className="text-xs text-muted-foreground">Total Workouts Completed</div>
      </CardContent>
    </Card>

    {/* Recent History */}
    <div>
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Recent Workouts</h3>
      {history.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Complete a workout to see your history</p>
        </div>
      ) : (
        <div className="space-y-2">
          {history.map((w) => (
            <Card key={w.id} className="border-border/50">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  {w.category === 'strength' ? <Dumbbell className="w-5 h-5 text-primary" /> : <Activity className="w-5 h-5 text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-foreground truncate">{w.routine_title}</h4>
                  <p className="text-xs text-muted-foreground">
                    {new Date(w.completed_at).toLocaleDateString()} &middot; {w.duration_minutes}m &middot; {w.exercises?.length || 0} exercises
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  </div>
);

// ===== Main Fitness Page =====
const Fitness = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mainTab, setMainTab] = useState('strength');
  const [view, setView] = useState('menu');
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedFocusAreas, setSelectedFocusAreas] = useState([]);
  const [fitnessLevel, setFitnessLevel] = useState('intermediate');
  const [duration, setDuration] = useState(20);
  const [generatedRoutine, setGeneratedRoutine] = useState(null);
  const [savedRoutines, setSavedRoutines] = useState([]);
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewingRoutine, setViewingRoutine] = useState(null);
  const [stats, setStats] = useState({ streak: 0, weekly_workouts: 0, weekly_duration: 0, weekly_volume: 0, total_workouts: 0 });
  const [workoutHistory, setWorkoutHistory] = useState([]);
  const [recovery, setRecovery] = useState({ recovery: {}, suggestion: '', recovered_areas: [], sore_areas: [] });
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [sharingWorkout, setSharingWorkout] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [loadingLinked, setLoadingLinked] = useState(false);
  const [practicePlan, setPracticePlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);

  const fetchSavedRoutines = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/fitness/routines`, { params: { category: mainTab } });
      setSavedRoutines(response.data.routines || []);
    } catch (error) { /* silent */ }
  }, [mainTab]);

  const fetchStats = useCallback(async () => {
    try {
      const [statsRes, histRes] = await Promise.all([
        axios.get(`${API}/fitness/stats`),
        axios.get(`${API}/fitness/workouts/history`, { params: { limit: 10 } })
      ]);
      setStats(statsRes.data);
      setWorkoutHistory(histRes.data.workouts || []);
    } catch (error) { /* silent */ }
  }, []);

  const fetchRecovery = useCallback(async () => {
    if (mainTab !== 'strength') return;
    try {
      const response = await axios.get(`${API}/fitness/recovery`);
      setRecovery(response.data);
    } catch (error) { /* silent */ }
  }, [mainTab]);

  useEffect(() => { fetchSavedRoutines(); }, [fetchSavedRoutines]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchRecovery(); }, [fetchRecovery]);

  // Handle deep links from social feed (view workout or start workout)
  useEffect(() => {
    const workoutId = searchParams.get('id') || searchParams.get('workout_id');
    const action = searchParams.get('action');
    const viewParam = searchParams.get('view');
    if (!workoutId) return;

    const loadLinkedWorkout = async () => {
      setLoadingLinked(true);
      try {
        const res = await axios.get(`${API}/fitness/workouts/${workoutId}`);
        const workout = res.data.workout;
        if (workout) {
          // Convert workout session into a routine-like object for RoutineView
          const routineFromWorkout = {
            id: workout.routine_id || workout.id,
            title: workout.routine_title || 'Shared Workout',
            category: workout.category || 'strength',
            subcategory: workout.subcategory || 'home',
            focus_areas: workout.focus_areas || [],
            fitness_level: 'intermediate',
            exercises: workout.exercises || [],
            warmup_notes: '',
            cooldown_notes: '',
          };
          setMainTab(workout.category || 'strength');
          setDuration(workout.duration_minutes || 20);
          setGeneratedRoutine(routineFromWorkout);
          setView('routine');
          // Clear params so it doesn't reload on re-render
          setSearchParams({}, { replace: true });
        }
      } catch (e) {
        toast.error('Could not load workout');
      } finally {
        setLoadingLinked(false);
      }
    };

    loadLinkedWorkout();
  }, [searchParams, setSearchParams]);

  const fetchExerciseLibrary = async () => {
    try {
      const response = await axios.get(`${API}/fitness/exercise-library`, { params: { category: mainTab } });
      setExerciseLibrary(response.data.exercises || []);
    } catch (error) { /* silent */ }
  };

  const handleSubcategorySelect = (sub) => { setSelectedSubcategory(sub); setSelectedFocusAreas([]); setView('configure'); };

  const toggleFocusArea = (areaId) => {
    if (areaId === 'full_body') {
      const allIds = FOCUS_AREAS.filter(a => a.id !== 'full_body').map(a => a.id);
      setSelectedFocusAreas(prev => prev.length === allIds.length ? [] : allIds);
      return;
    }
    setSelectedFocusAreas(prev => prev.includes(areaId) ? prev.filter(a => a !== areaId) : [...prev, areaId]);
  };

  const getDurations = () => {
    if (mainTab === 'strength') return STRENGTH_DURATIONS;
    return MOBILITY_DURATIONS[selectedSubcategory] || STRENGTH_DURATIONS;
  };

  const generateRoutine = async () => {
    if (selectedFocusAreas.length === 0) { toast.error('Select at least one focus area'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/fitness/generate-routine`, {
        category: mainTab, subcategory: selectedSubcategory,
        focus_areas: selectedFocusAreas, fitness_level: fitnessLevel, duration_minutes: duration,
      });
      if (response.data.success) { setGeneratedRoutine(response.data.routine); setView('routine'); toast.success('Routine generated!'); }
    } catch (error) { toast.error('Failed to generate routine. Please try again.'); }
    finally { setLoading(false); }
  };

  const saveRoutine = async () => {
    if (!generatedRoutine) return;
    setSaving(true);
    try {
      const response = await axios.post(`${API}/fitness/routines/save`, {
        title: generatedRoutine.title, category: generatedRoutine.category, subcategory: generatedRoutine.subcategory,
        focus_areas: generatedRoutine.focus_areas, fitness_level: generatedRoutine.fitness_level,
        exercises: generatedRoutine.exercises, warmup_notes: generatedRoutine.warmup_notes, cooldown_notes: generatedRoutine.cooldown_notes,
      });
      if (response.data.success) { toast.success('Routine saved!'); fetchSavedRoutines(); }
    } catch (error) { toast.error('Failed to save routine'); }
    finally { setSaving(false); }
  };

  const deleteRoutine = async (routineId) => {
    try {
      await axios.delete(`${API}/fitness/routines/${routineId}`);
      toast.success('Routine removed'); fetchSavedRoutines(); setViewingRoutine(null); setView('saved');
    } catch (error) { toast.error('Failed to delete routine'); }
  };

  const completeWorkout = async (exercises) => {
    const currentRoutine = viewingRoutine || generatedRoutine;
    if (!currentRoutine) return;
    // Collect all muscle groups from exercises
    const allMuscles = [];
    exercises.forEach(ex => { (ex.muscle_groups || []).forEach(mg => { if (!allMuscles.includes(mg)) allMuscles.push(mg); }); });
    try {
      const res = await axios.post(`${API}/fitness/workouts/log`, {
        routine_id: currentRoutine.id || null, routine_title: currentRoutine.title,
        category: currentRoutine.category, subcategory: currentRoutine.subcategory,
        exercises, duration_minutes: duration || 20,
        focus_areas: currentRoutine.focus_areas || [], muscle_groups_worked: allMuscles,
      });
      toast.success('Workout logged! Great job!');
      fetchStats(); fetchRecovery();
      setSharingWorkout({
        title: currentRoutine.title,
        category: currentRoutine.category,
        subcategory: currentRoutine.subcategory,
        duration: duration || 20,
        exerciseCount: exercises.length,
        focusAreas: currentRoutine.focus_areas || [],
        workoutId: res.data.workout?.id || null,
      });
      setShowShareDialog(true);
    } catch (error) { toast.error('Failed to log workout'); }
  };

  const shareWorkoutToFeed = async () => {
    if (!sharingWorkout) return;
    setSharing(true);
    try {
      const focusStr = sharingWorkout.focusAreas.length > 0 ? sharingWorkout.focusAreas.join(', ') : sharingWorkout.category;
      const content = `Just completed "${sharingWorkout.title}"! ${sharingWorkout.duration} min ${sharingWorkout.category} workout with ${sharingWorkout.exerciseCount} exercises targeting ${focusStr}.`;
      await axios.post(`${API}/social/posts`, {
        content,
        post_type: 'workout',
        media_urls: [],
        workout_id: sharingWorkout.workoutId
      });
      toast.success('Shared to your feed!');
      setShowShareDialog(false);
      setSharingWorkout(null);
    } catch (e) { toast.error('Failed to share'); }
    finally { setSharing(false); }
  };

  const resetToMenu = () => { setView('menu'); setSelectedSubcategory(null); setSelectedFocusAreas([]); setGeneratedRoutine(null); setViewingRoutine(null); };
  const handleTabChange = (val) => { setMainTab(val); resetToMenu(); };

  const generatePracticePlan = async () => {
    setLoadingPlan(true);
    try {
      const res = await axios.post(`${API}/fitness/practice-plan`);
      if (res.data.success) { setPracticePlan(res.data); toast.success('Practice plan generated!'); }
    } catch (e) { toast.error('Failed to generate practice plan'); }
    finally { setLoadingPlan(false); }
  };

  // ===== Render: Practice Plan =====
  const renderPracticePlan = () => {
    const CATEGORY_COLORS = {
      off_the_tee: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
      approach: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
      short_game: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
      putting: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
    };

    return (
      <div className="space-y-4">
        {!practicePlan ? (
          <div className="space-y-5">
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">AI Practice Plan</h3>
              <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                Generate a targeted practice session based on your Strokes Gained analysis. The AI will focus on your weakest areas.
              </p>
            </div>
            <Button
              className="w-full h-12 text-base font-semibold rounded-xl"
              onClick={generatePracticePlan}
              disabled={loadingPlan}
              data-testid="generate-practice-plan-btn"
            >
              {loadingPlan ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analyzing your game...</> : <><Sparkles className="w-5 h-5 mr-2" /> Generate Practice Plan</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="practice-plan-view">
            {/* Plan Header */}
            <div className="bg-card rounded-2xl border border-border/50 p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-bold text-foreground">{practicePlan.plan?.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">Focus: {practicePlan.plan?.focus}</p>
                </div>
                <Badge variant="outline" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />{practicePlan.plan?.duration_minutes || 45} min
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{practicePlan.plan?.summary}</p>

              {/* SG Summary Bar */}
              {practicePlan.strokes_gained_summary && (
                <div className="mt-4 pt-3 border-t border-border/30">
                  <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Your Strokes Gained</div>
                  <div className="flex gap-2">
                    {[
                      { key: 'off_the_tee', label: 'Tee' },
                      { key: 'approach', label: 'App' },
                      { key: 'around_green', label: 'Short' },
                      { key: 'putting', label: 'Putt' },
                    ].map(cat => {
                      const val = practicePlan.strokes_gained_summary[cat.key];
                      const isWorst = cat.key === practicePlan.worst_area;
                      return (
                        <div key={cat.key} className={`flex-1 text-center py-1.5 rounded-lg text-xs font-bold ${isWorst ? 'bg-red-500/10 text-red-500 ring-1 ring-red-500/30' : 'bg-muted/50 text-muted-foreground'}`}>
                          <div>{cat.label}</div>
                          <div className="text-[10px]">{val > 0 ? '+' : ''}{val.toFixed(1)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Warmup */}
            {practicePlan.plan?.warmup && (
              <div className="bg-muted/30 rounded-xl p-3 border border-border/30">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Warmup</span>
                <p className="text-sm text-foreground mt-1">{practicePlan.plan.warmup}</p>
              </div>
            )}

            {/* Drills */}
            <div className="space-y-3">
              {(practicePlan.plan?.drills || []).map((drill, i) => (
                <div key={i} className="bg-card rounded-xl border border-border/50 overflow-hidden" data-testid={`drill-${i}`}>
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-sm font-black text-primary flex-shrink-0">{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h4 className="text-sm font-bold text-foreground">{drill.name}</h4>
                          {drill.category && (
                            <span className={`text-[9px] px-2 py-0.5 rounded-full border font-bold ${CATEGORY_COLORS[drill.category] || 'bg-muted text-muted-foreground'}`}>
                              {drill.category.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{drill.description}</p>
                        <div className="flex gap-3 text-[10px] text-muted-foreground">
                          {drill.duration_minutes && <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> {drill.duration_minutes} min</span>}
                          {drill.reps_or_balls && <span>{drill.reps_or_balls}</span>}
                        </div>
                        {drill.instructions && (
                          <ol className="mt-2 space-y-1 text-xs text-muted-foreground list-decimal list-inside">
                            {drill.instructions.map((step, j) => <li key={j}>{step}</li>)}
                          </ol>
                        )}
                        {drill.success_metric && (
                          <div className="mt-2 flex items-center gap-1.5 text-[10px] text-emerald-600 bg-emerald-500/10 rounded-lg px-2.5 py-1.5 border border-emerald-500/20">
                            <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                            <span className="font-semibold">Goal: {drill.success_metric}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Cooldown */}
            {practicePlan.plan?.cooldown && (
              <div className="bg-muted/30 rounded-xl p-3 border border-border/30">
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Cooldown</span>
                <p className="text-sm text-foreground mt-1">{practicePlan.plan.cooldown}</p>
              </div>
            )}

            {/* Regenerate */}
            <Button variant="outline" className="w-full rounded-xl" onClick={() => { setPracticePlan(null); generatePracticePlan(); }} disabled={loadingPlan} data-testid="regenerate-plan-btn">
              {loadingPlan ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Replace className="w-4 h-4 mr-2" />} New Plan
            </Button>
          </div>
        )}
      </div>
    );
  };

  // ===== Render: Menu =====
  const renderMenu = () => {
    const subcats = SUBCATEGORY_INFO[mainTab];
    if (!subcats) return null;
    return (
      <div className="space-y-4">
        {/* Streak Banner */}
        {stats.streak > 0 && (
          <Card className="border-orange-500/30 bg-gradient-to-r from-orange-500/10 to-yellow-500/10">
            <CardContent className="p-3 flex items-center gap-3">
              <Flame className="w-8 h-8 text-orange-500" />
              <div>
                <div className="text-lg font-bold text-foreground">{stats.streak} Day Streak!</div>
                <div className="text-xs text-muted-foreground">Keep it going — consistency builds champions</div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recovery Suggestion (strength only) */}
        {mainTab === 'strength' && recovery.suggestion && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1"><Heart className="w-4 h-4 text-primary" /><span className="text-sm font-semibold text-primary">Recovery Insight</span></div>
              <p className="text-sm text-muted-foreground">{recovery.suggestion}</p>
            </CardContent>
          </Card>
        )}

        <div>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Choose Your Focus</h3>
          <div className="space-y-2">
            {Object.entries(subcats).map(([key, info]) => {
              const Icon = info.icon;
              return (
                <button key={key} onClick={() => handleSubcategorySelect(key)} className="w-full flex items-center gap-4 p-4 rounded-2xl bg-card border border-border/50 hover:border-primary/40 hover:bg-primary/5 transition-all duration-200 text-left group" data-testid={`subcategory-${key}`}>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors"><Icon className="w-6 h-6 text-primary" /></div>
                  <div className="flex-1"><h4 className="font-semibold text-foreground">{info.label}</h4><p className="text-sm text-muted-foreground">{info.desc}</p></div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => { fetchStats(); setView('progress'); }} data-testid="view-progress-btn">
            <TrendingUp className="w-4 h-4 mr-2" />Progress
          </Button>
          <Button variant="outline" className="flex-1" onClick={() => { fetchExerciseLibrary(); setView('library'); }} data-testid="browse-library-btn">
            <Target className="w-4 h-4 mr-2" />Library
          </Button>
        </div>

        {/* Saved Routines */}
        {savedRoutines.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Saved Routines</h3>
              <Button variant="ghost" size="sm" onClick={() => setView('saved')} data-testid="view-all-saved">View All <ChevronRight className="w-4 h-4 ml-1" /></Button>
            </div>
            <div className="space-y-2">
              {savedRoutines.slice(0, 3).map((routine) => (
                <button key={routine.id} onClick={() => { setViewingRoutine(routine); setView('routine'); }} className="w-full flex items-center gap-3 p-3 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all text-left" data-testid={`saved-routine-${routine.id}`}>
                  <Bookmark className="w-5 h-5 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0"><h4 className="text-sm font-medium text-foreground truncate">{routine.title}</h4><p className="text-xs text-muted-foreground capitalize">{routine.subcategory?.replace('_', '-')} &middot; {routine.exercises?.length || 0} exercises</p></div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ===== Render: Configure =====
  const renderConfigure = () => {
    const subInfo = SUBCATEGORY_INFO[mainTab][selectedSubcategory];
    const durations = getDurations();
    const allNonFullBody = FOCUS_AREAS.filter(a => a.id !== 'full_body').map(a => a.id);
    const isFullBodySelected = allNonFullBody.every(id => selectedFocusAreas.includes(id));

    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={resetToMenu} data-testid="config-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div><h2 className="text-lg font-bold text-foreground">{subInfo?.label}</h2><p className="text-sm text-muted-foreground">{subInfo?.desc}</p></div>
        </div>

        {/* Focus Areas with Recovery Indicators (strength only) */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">What do you want to focus on?</h3>
          <div className="grid grid-cols-3 gap-2">
            {FOCUS_AREAS.map((area) => {
              const isSelected = area.id === 'full_body' ? isFullBodySelected : selectedFocusAreas.includes(area.id);
              const rec = mainTab === 'strength' && area.id !== 'full_body' ? recovery.recovery?.[area.id] : null;
              return (
                <button key={area.id} onClick={() => toggleFocusArea(area.id)}
                  className={`p-3 rounded-xl border text-center transition-all duration-200 relative ${isSelected ? 'border-primary bg-primary/10 text-primary' : 'border-border/50 bg-card text-muted-foreground hover:border-primary/30'}`}
                  data-testid={`focus-area-${area.id}`}>
                  {area.id === 'full_body' ? <Sparkles className={`w-5 h-5 mx-auto mb-1 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} /> : <Target className={`w-5 h-5 mx-auto mb-1 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />}
                  <span className="text-xs font-medium block">{area.label}</span>
                  {rec && rec.status !== 'recovered' && (
                    <span className={`text-[9px] mt-0.5 block font-medium ${rec.status === 'sore' ? 'text-red-500' : 'text-yellow-500'}`}>{RECOVERY_LABELS[rec.status]}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Fitness Level */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Fitness Level</h3>
          <div className="flex gap-2">
            {FITNESS_LEVELS.map((level) => (
              <button key={level.id} onClick={() => setFitnessLevel(level.id)}
                className={`flex-1 p-3 rounded-xl border text-center transition-all duration-200 ${fitnessLevel === level.id ? 'border-primary bg-primary/10 text-primary' : 'border-border/50 bg-card text-muted-foreground hover:border-primary/30'}`}
                data-testid={`fitness-level-${level.id}`}>
                <span className="text-sm font-medium block">{level.label}</span>
                <span className="text-[10px] opacity-70">{level.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Duration */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3"><Clock className="w-4 h-4 inline mr-1.5" />Duration</h3>
          <div className="flex gap-2 flex-wrap">
            {durations.map((d) => (
              <button key={d} onClick={() => setDuration(d)}
                className={`px-4 py-2.5 rounded-xl border text-center text-sm font-medium transition-all duration-200 ${duration === d ? 'border-primary bg-primary/10 text-primary' : 'border-border/50 bg-card text-muted-foreground hover:border-primary/30'}`}
                data-testid={`duration-${d}`}>{d}m</button>
            ))}
          </div>
        </div>

        <Button className="w-full h-12 text-base font-semibold bg-primary hover:bg-primary/90" onClick={generateRoutine} disabled={loading || selectedFocusAreas.length === 0} data-testid="generate-routine-btn">
          {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating Your Routine...</> : <><Sparkles className="w-5 h-5 mr-2" />Generate Routine</>}
        </Button>
      </div>
    );
  };

  // ===== Render: Saved List =====
  const renderSavedList = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={resetToMenu} data-testid="saved-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <h2 className="text-lg font-bold text-foreground">Saved Routines</h2>
      </div>
      {savedRoutines.length === 0 ? (
        <div className="text-center py-12"><Bookmark className="w-16 h-16 mx-auto mb-3 text-muted-foreground/30" /><h3 className="text-lg font-semibold text-foreground mb-1">No saved routines yet</h3><p className="text-sm text-muted-foreground">Generate a routine and save it to access it later</p></div>
      ) : (
        <div className="space-y-2">
          {savedRoutines.map((routine) => (
            <button key={routine.id} onClick={() => { setViewingRoutine(routine); setView('routine'); }} className="w-full flex items-center gap-3 p-4 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all text-left" data-testid={`saved-routine-item-${routine.id}`}>
              <Bookmark className="w-5 h-5 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0"><h4 className="text-sm font-semibold text-foreground truncate">{routine.title}</h4><p className="text-xs text-muted-foreground capitalize">{routine.subcategory?.replace('_', '-')} &middot; {routine.exercises?.length || 0} exercises &middot; {routine.fitness_level}</p>
                <div className="flex gap-1 mt-1.5">{(routine.focus_areas || []).map((a, i) => <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{a}</Badge>)}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ===== Render: Library =====
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryFocus, setLibraryFocus] = useState('all');

  const libraryView = useMemo(() => {
    const search = librarySearch.toLowerCase();
    const filtered = exerciseLibrary.filter(ex => {
      if (libraryFocus !== 'all' && ex.focus_area !== libraryFocus) return false;
      if (search && !ex.name.toLowerCase().includes(search) && !(ex.description || '').toLowerCase().includes(search)) return false;
      return true;
    });
    const grouped = {};
    filtered.forEach(ex => { const area = ex.focus_area || 'other'; if (!grouped[area]) grouped[area] = []; grouped[area].push(ex); });
    const focusCounts = {};
    exerciseLibrary.forEach(ex => { if (ex.focus_area) focusCounts[ex.focus_area] = (focusCounts[ex.focus_area] || 0) + 1; });
    const focusAreas = Object.keys(focusCounts).sort();
    const groupedSorted = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
    return { filtered, groupedSorted, focusAreas, focusCounts };
  }, [exerciseLibrary, libraryFocus, librarySearch]);

  const renderLibrary = () => {
    const { filtered, groupedSorted, focusAreas, focusCounts } = libraryView;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={resetToMenu} data-testid="library-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h2 className="text-lg font-bold text-foreground">Exercise Library</h2>
            <p className="text-sm text-muted-foreground capitalize">{exerciseLibrary.length} {mainTab} exercises for golf</p>
          </div>
        </div>
        <input
          type="text" placeholder="Search exercises..." value={librarySearch}
          onChange={(e) => setLibrarySearch(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid="library-search"
        />
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setLibraryFocus('all')} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${libraryFocus === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-testid="library-filter-all">All ({exerciseLibrary.length})</button>
          {focusAreas.map(area => {
            const count = focusCounts[area];
            return (
              <button key={area} onClick={() => setLibraryFocus(area)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize transition-colors ${libraryFocus === area ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-testid={`library-filter-${area}`}>{area} ({count})</button>
            );
          })}
        </div>
        {groupedSorted.map(([area, exercises]) => (
          <div key={area}>
            <h3 className="text-sm font-semibold text-primary uppercase tracking-wider mb-2 capitalize">{area} ({exercises.length})</h3>
            <div className="space-y-2">{exercises.map((ex, i) => <ExerciseCard key={ex.id || i} exercise={ex} index={i} />)}</div>
          </div>
        ))}
        {filtered.length === 0 && exerciseLibrary.length > 0 && (
          <div className="text-center py-8"><p className="text-sm text-muted-foreground">No exercises match your search</p></div>
        )}
        {exerciseLibrary.length === 0 && (
          <div className="text-center py-12"><Target className="w-16 h-16 mx-auto mb-3 text-muted-foreground/30" /><h3 className="text-lg font-semibold text-foreground mb-1">Library is empty</h3><p className="text-sm text-muted-foreground">Generate a routine to populate the exercise library</p></div>
        )}
      </div>
    );
  };

  const currentRoutine = viewingRoutine || generatedRoutine;
  const isSavedRoutine = viewingRoutine != null;

  const renderContent = () => {
    if (view === 'progress') return <ProgressView onBack={resetToMenu} stats={stats} history={workoutHistory} />;
    if (view === 'menu') return renderMenu();
    if (view === 'configure') return renderConfigure();
    if (view === 'routine' && currentRoutine) return <RoutineView routine={currentRoutine} onSave={saveRoutine} onBack={resetToMenu} isSaved={isSavedRoutine} onDelete={() => deleteRoutine(viewingRoutine?.id)} saving={saving} onComplete={completeWorkout} exerciseLibrary={exerciseLibrary} onFetchLibrary={fetchExerciseLibrary} />;
    if (view === 'saved') return renderSavedList();
    if (view === 'library') return renderLibrary();
    return renderMenu();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="fitness-page">
      <div className="max-w-lg mx-auto px-4 pt-6 pb-24">
        <div className="mb-5">
          <h1 className="font-serif text-2xl font-bold" style={{ color: 'var(--deep-forest)' }}>Golf Fitness</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--warm-gray)' }}>AI-powered routines to improve your game</p>
        </div>

        <Tabs value={mainTab} onValueChange={handleTabChange} className="mb-5">
          <TabsList className="w-full grid grid-cols-3 h-11" data-testid="fitness-main-tabs">
            <TabsTrigger value="strength" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-semibold" data-testid="tab-strength"><Dumbbell className="w-4 h-4 mr-1.5" />Strength</TabsTrigger>
            <TabsTrigger value="mobility" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-semibold" data-testid="tab-mobility"><Activity className="w-4 h-4 mr-1.5" />Mobility</TabsTrigger>
            <TabsTrigger value="practice" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-semibold" data-testid="tab-practice"><Target className="w-4 h-4 mr-1.5" />Practice</TabsTrigger>
          </TabsList>
          <TabsContent value="strength" className="mt-4">{renderContent()}</TabsContent>
          <TabsContent value="mobility" className="mt-4">{renderContent()}</TabsContent>
          <TabsContent value="practice" className="mt-4">{renderPracticePlan()}</TabsContent>
        </Tabs>
      </div>

      {/* Share Workout Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl border-border/50">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-primary" /> Share Your Workout
            </DialogTitle>
          </DialogHeader>
          {sharingWorkout && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-2xl p-4 text-center">
                <p className="text-base font-semibold text-foreground mb-1">{sharingWorkout.title}</p>
                <div className="flex justify-center gap-4 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {sharingWorkout.duration} min</span>
                  <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3" /> {sharingWorkout.exerciseCount} exercises</span>
                </div>
                {sharingWorkout.focusAreas.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1 mt-3">
                    {sharingWorkout.focusAreas.map(a => (
                      <span key={a} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs capitalize">{a}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowShareDialog(false)} className="flex-1 rounded-full" data-testid="skip-share-workout-btn">
                  Skip
                </Button>
                <Button onClick={shareWorkoutToFeed} disabled={sharing} className="flex-1 rounded-full gap-2" data-testid="share-workout-btn">
                  <Share2 className="w-4 h-4" /> {sharing ? 'Sharing...' : 'Share to Feed'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TooltipGuide pageKey="fitness" />
      <Navigation />
    </div>
  );
};

export default Fitness;
